// frontend/src/utils/matchRoster.js
// Scores parsed roster entries against mirrored Sportradar persons.
//
// Pure functions with no network access, so the thresholds can be tested against the
// real false positives we measured rather than guessed at.
//
// Nothing here decides anything. It ranks candidates and labels its confidence; every
// link is confirmed by a person before it is written.

/** Fold accents, case and punctuation so "Ng'uni" and "Nguni" compare equal. */
export const normalizeName = (value) =>
    (value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const tokensOf = (value) => normalizeName(value).split(' ').filter(Boolean);

/** Padded character trigrams, the same shape pg_trgm uses. */
const trigrams = (token) => {
    const padded = `  ${token} `;
    const out = new Set();
    for (let i = 0; i < padded.length - 2; i++) out.add(padded.slice(i, i + 3));
    return out;
};

/**
 * Similarity between two single name tokens.
 *
 * Needed because the two lists disagree with each other on spelling: the same player is
 * "Mundiya Mabuku" on one and "Mundia Mabuku" on the other. Exact token matching scores
 * that pair identically to two unrelated people who happen to share a family name, so
 * the two cases cannot be separated without looking inside the token.
 */
export const tokenSimilarity = (a, b) => {
    if (a === b) return 1;
    const A = trigrams(a);
    const B = trigrams(b);
    let shared = 0;
    for (const g of A) if (B.has(g)) shared++;
    return (2 * shared) / (A.size + B.size);
};

// mundiya/mundia sits at 0.67 and brenan/brenda at 0.57, so the boundary falls between
// a spelling variant of one name and two different names that merely start alike.
const SAME_TOKEN = 0.6;

/**
 * Dice coefficient over name tokens, order-insensitive — the roster writes "Daren Musa"
 * and Sportradar writes "MUSA Daren".
 *
 * Both tokens shared scores 1.0; one of two scores 0.5. That gap drives the thresholds
 * below: in the Zambia probe every 0.5 was a different person who merely shared a family
 * or given name — "Adson Bwalya" against "BWALYA Gift".
 */
export const nameScore = (a, b) => {
    const A = tokensOf(a);
    const B = tokensOf(b);
    if (!A.length || !B.length) return 0;

    // Greedy best-first pairing. Names are two or three tokens, so the simplicity is
    // worth more than the optimal assignment an exhaustive match would find.
    const remaining = [...B];
    let shared = 0;
    for (const token of A) {
        let bestAt = -1;
        let bestSim = SAME_TOKEN;
        remaining.forEach((candidate, i) => {
            const sim = tokenSimilarity(token, candidate);
            if (sim >= bestSim) { bestSim = sim; bestAt = i; }
        });
        if (bestAt !== -1) { shared++; remaining.splice(bestAt, 1); }
    }
    return (2 * shared) / (A.length + B.length);
};

// A name alone has to be near-exact. Anything looser was wrong every time we measured it.
const STRONG_NAME = 0.85;
// With birth dates agreeing, a weaker name is still good evidence.
const CORROBORATED_NAME = 0.6;
const WORTH_SHOWING = 0.5;

const compareDob = (entryDob, personDob) => {
    if (!entryDob || !personDob) return 'unknown';
    return entryDob === personDob ? 'match' : 'conflict';
};

/**
 * Confidence for one entry/person pairing.
 *
 * A conflicting date of birth caps the result at 'review' however well the names agree.
 * BWALYA Elvis matched a roster entry exactly by name while the birth dates differed by
 * six months — either Sportradar is wrong or it is a different person, and neither is
 * something to resolve without a human.
 */
export const gradeMatch = (score, dobStatus) => {
    if (dobStatus === 'conflict') return score >= STRONG_NAME ? 'review' : 'none';
    if (dobStatus === 'match' && score >= CORROBORATED_NAME) return 'strong';
    if (score >= STRONG_NAME) return 'strong';
    if (score >= WORTH_SHOWING) return 'review';
    return 'none';
};

/** Highest-scoring pairing across every name form on both sides. */
const bestPairing = (entry, person) => {
    const mine = entry.nameCandidates?.length ? entry.nameCandidates : [entry.fullName];
    const theirs = [person.name_full_latin, person.name_full_local].filter(Boolean);

    let best = 0;
    for (const a of mine) {
        for (const b of theirs) {
            const score = nameScore(a, b);
            if (score > best) best = score;
        }
    }
    return best;
};

/**
 * Rank candidates for each entry.
 *
 * Returns one row per entry: { entry, candidates: [{ person, score, dobStatus, tier }] },
 * best first, capped at `limit`. Entries with nothing worth showing get an empty list.
 */
export const matchRoster = (entries, persons, { limit = 3 } = {}) =>
    entries.map((entry) => {
        const scored = [];

        for (const person of persons) {
            const score = bestPairing(entry, person);
            if (score < WORTH_SHOWING) continue;

            const dobStatus = compareDob(entry.dob, person.dob);
            const tier = gradeMatch(score, dobStatus);
            if (tier === 'none') continue;

            scored.push({ person, score, dobStatus, tier });
        }

        // Best name first; a confirmed birth date breaks ties, since it is the stronger
        // signal when two candidates read alike.
        scored.sort((a, b) =>
            b.score - a.score ||
            (a.dobStatus === 'match' ? -1 : 0) - (b.dobStatus === 'match' ? -1 : 0)
        );

        return { entry, candidates: scored.slice(0, limit) };
    });

/**
 * Whether a mirrored person appears to have a photograph at all.
 *
 * Only that a URL exists — most of them resolve to one of two shared silhouette
 * placeholders, and nothing in the record distinguishes those from a real photograph
 * (identical PERSON_HEAD type, identical 130x130). The review screen renders the
 * thumbnail so the difference is visible, which beats any guess made from metadata.
 */
export const hasPhoto = (person) => !!person?.current_photo_url;

const isShouted = (token) => token === token.toUpperCase() && /[A-Z]/.test(token);
const titleCase = (token) =>
    isShouted(token) ? token.charAt(0) + token.slice(1).toLowerCase() : token;

/**
 * The name a linked player is stored under.
 *
 * Sportradar wins once a link is confirmed: it is the system of record for the identity
 * the photo is filed against, and the app's existing linked players already carry its
 * form — "ABBAS Sohail", "MWEWA Mary". Keeping the spreadsheet's spelling would leave two
 * conventions in one roster and split the accreditation export's grouping.
 *
 * name_full_latin is absent for roughly a fifth of records, so the local-script name is
 * the fallback.
 *
 * The given and family parts have to be derived. raw_data carries nameGivenLatin and
 * nameFamilyLatin on every record but Sportradar leaves both null on all 35,148 of them,
 * so the split follows the shape of the full name instead: leading shouted tokens are the
 * family name, title-cased, matching how the app's existing linked rows are already
 * stored ("ABDU RAUF Muhajir" → Muhajir / Abdu Rauf).
 */
export const resolvePersonName = (person) => {
    const fullName = person?.name_full_latin || person?.name_full_local || null;
    if (!fullName) return { fullName: null, firstName: null, lastName: null };

    const tokens = fullName.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 1) {
        const only = titleCase(tokens[0]);
        return { fullName, firstName: only, lastName: only };
    }

    let familyCount = 0;
    while (familyCount < tokens.length && isShouted(tokens[familyCount])) familyCount++;

    // "ABDUL THAQIF" is shouted throughout; the app stores it as Thaqif / Abdul, so an
    // all-shouted name keeps only its first token as the family name. Nothing shouted at
    // all still reads as family-first, which is the form Sportradar uses.
    if (familyCount === 0 || familyCount === tokens.length) familyCount = 1;

    return {
        fullName,
        firstName: tokens.slice(familyCount).map(titleCase).join(' '),
        lastName: tokens.slice(0, familyCount).map(titleCase).join(' '),
    };
};
